import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownButton,
  IconButton,
  MenuItem,
  Switch,
} from '@common/material'
import { ButtonGroup } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import MenuOpenOutlined from '@mui/icons-material/MenuOpenOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import Place from '@mui/icons-material/Place'
import CarouselNavigation from '@common/CarouselNavigation'
import { ModalDialogContainer } from '@common/modal/ModalDialogContainer'
import { Box, Flex, Image, Text } from '@common/fundamentals'
import { useModal } from '..'
import axios from 'axios'
import fileDownload from 'js-file-download'
import { localizedStrings } from '@core/strings'
import { useToast } from '@core/toast'
import { ObjectDetectionRobotChip } from '@elements/ObjectDetection/ObjectDetectionRobotChip'
import {
  DetectedObjects,
  TemplateWarningItems,
  warningItemColors,
} from '@core/entities/template/TemplateWarnings/types'
import { MediaGeoJSON } from '@core/entities/template/TemplateQuestion/types'
import isEqual from 'lodash/isEqual'
import { useDeepCompareMemo } from 'use-deep-compare'
import moment from 'moment'
import { MapBoxMap } from '@common/material/MapBox'
import type { MapRef } from 'react-map-gl'
import { Marker } from 'react-map-gl'
import useConstants from '@core/providers/useConstants'

type ImageSize = {
  containerWidth: number
  containerHeight: number
  naturalWidth: number
  naturalHeight: number
}

type ObjectVisibility = {
  [K in TemplateWarningItems]?: boolean
}

const useImageBoundingBoxes = (
  imageSize: ImageSize,
  s3Key: string | undefined,
  objectDetections: Array<{ detectedObjects?: DetectedObjects }>,
  visibleObjects: ObjectVisibility,
) => {
  const getDisplayedDimensions = () => {
    const { containerWidth, containerHeight, naturalWidth, naturalHeight } =
      imageSize

    // Calculate the scaling ratios
    const containerRatio = containerWidth / containerHeight
    const imageRatio = naturalWidth / naturalHeight

    let displayedWidth, displayedHeight

    if (imageRatio > containerRatio) {
      // Image is wider than container ratio - will be constrained by width
      displayedWidth = containerWidth
      displayedHeight = containerWidth / imageRatio
    } else {
      // Image is taller than container ratio - will be constrained by height
      displayedHeight = containerHeight
      displayedWidth = containerHeight * imageRatio
    }

    // Calculate the offset to center the image
    const left = (containerWidth - displayedWidth) / 2
    const top = (containerHeight - displayedHeight) / 2

    return { displayedWidth, displayedHeight, left, top }
  }

  const renderBoundingBoxes = () => {
    if (!imageSize.naturalWidth || !imageSize.naturalHeight) return []

    const { displayedWidth, displayedHeight, left, top } =
      getDisplayedDimensions()

    return objectDetections.flatMap(
      (warning, warningIndex) =>
        warning.detectedObjects?.[s3Key]?.flatMap((object, objectIndex) =>
          visibleObjects[object.displayName]
            ? object.boundingBoxes.map((box, boxIndex) => {
                // Calculate the scale based on the displayed dimensions
                const scaleX = displayedWidth / imageSize.naturalWidth
                const scaleY = displayedHeight / imageSize.naturalHeight

                // Apply the scale to the original coordinates and add the offset
                const scaledX = box.xMin * scaleX + left
                const scaledY = box.yMin * scaleY + top
                const scaledWidth = (box.xMax - box.xMin) * scaleX
                const scaledHeight = (box.yMax - box.yMin) * scaleY

                const colors =
                  warningItemColors[object.item] || warningItemColors.ALL_ITEMS

                return (
                  <Box
                    key={`${warningIndex}-${objectIndex}-${boxIndex}`}
                    position='absolute'
                    sx={{
                      left: scaledX,
                      top: scaledY,
                      width: scaledWidth,
                      height: scaledHeight,
                      border: `2px solid ${colors.border}`,
                      backgroundColor: colors.background,
                      '&::before, &::after': {
                        content: `"${object.displayName}"`,
                        position: 'absolute',
                        padding: '4px 8px',
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '&::before': {
                        backgroundColor: colors.border,
                        color: 'transparent',
                      },
                      '&::after': {
                        color: '#fff',
                      },
                    }}
                  />
                )
              })
            : [],
        ) || [],
    )
  }

  return { renderBoundingBoxes }
}

type ComponentType = {
  title: string
  s3Key?: string
  previewUrl: string
  images?: Array<{
    uri?: string
    downloadUrl?: string
    getUrl?: string
    key?: string
    geoJSON?: MediaGeoJSON
  }>
  footerActions?: ReactNode
  canDownload?: boolean
  objectDetections?: Array<{ detectedObjects?: DetectedObjects }>
  preventDialogClose?: boolean
  onDownloadAll?: () => void
  capturedByName?: string
}

const ImageModal: React.FC<ComponentType> = ({
  title,
  s3Key,
  previewUrl,
  images,
  footerActions,
  canDownload,
  objectDetections = [],
  preventDialogClose,
  onDownloadAll,
  capturedByName,
}) => {
  const toast = useToast()
  const { constants } = useConstants()
  const { hideModal } = useModal()
  const [expanded, setExpanded] = useState(true)
  const [visibleObjects, setVisibleObjects] = useState<ObjectVisibility>({})
  const mapRef = useRef<MapRef | undefined>(undefined)

  // Use images array if provided, otherwise fall back to single previewUrl
  const imageArray = images || [{ uri: previewUrl, key: s3Key }]
  const hasMultipleImages = imageArray.length > 1

  // Find the index of the previewUrl in the images array
  const initialImageIndex = imageArray.findIndex(
    (image) =>
      image.uri === previewUrl ||
      image.downloadUrl === previewUrl ||
      image.getUrl === previewUrl,
  )

  const [currentImageIndex, setCurrentImageIndex] = useState(
    initialImageIndex >= 0 ? initialImageIndex : 0,
  )
  const currentImage = imageArray[currentImageIndex]
  const currentImageUrl =
    currentImage?.uri || currentImage?.downloadUrl || currentImage?.getUrl

  const currentImageKey = currentImage?.key || s3Key

  const getLocationFromGeoJSON = (geoJSONData: MediaGeoJSON | undefined) => {
    if (!geoJSONData?.features?.length) return null

    const feature = geoJSONData.features[0]
    const geometry = feature?.geometry
    const properties = feature?.properties as
      | Record<string, unknown>
      | undefined

    if (
      !geometry ||
      geometry.type !== 'Point' ||
      !Array.isArray(geometry.coordinates)
    )
      return null

    const [longitude, latitude] = geometry.coordinates

    return {
      latitude: Number(latitude),
      longitude: Number(longitude),
      accuracy:
        (properties?.locationAccuracyInMeters as number | undefined) ??
        undefined,
      photoTakenOn: properties?.photoTakenOn as string | undefined,
    }
  }

  const locationData = useMemo(
    () => getLocationFromGeoJSON(currentImage?.geoJSON),
    [currentImage?.geoJSON],
  )

  const hasLocationMetadata = !!locationData || !!capturedByName

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? imageArray.length - 1 : prev - 1,
    )
  }

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === imageArray.length - 1 ? 0 : prev + 1,
    )
  }
  const [imageSize, setImageSize] = useState<ImageSize>({
    containerWidth: 0,
    containerHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
  })

  const { renderBoundingBoxes } = useImageBoundingBoxes(
    imageSize,
    currentImageKey,
    objectDetections,
    visibleObjects,
  )

  // Get unique object types from all warnings
  const detectedObjectTypes = useDeepCompareMemo(() => {
    if (!s3Key) return []

    // Get all detected objects
    const allObjects = objectDetections.flatMap(
      (detection) => detection.detectedObjects?.[s3Key] || [],
    )

    // Get all display names
    const allDisplayNames = allObjects.map(
      (obj) => obj.displayName as TemplateWarningItems,
    )

    // Filter to unique names only
    return Array.from(new Set(allDisplayNames))
  }, [objectDetections, s3Key])

  // Initialize visibility state when object types are computed
  useEffect(() => {
    const initialVisibility: ObjectVisibility = {}
    detectedObjectTypes.forEach((type) => {
      initialVisibility[type] = true
    })
    setVisibleObjects(initialVisibility)
  }, [detectedObjectTypes])

  // Check if all objects are visible
  const areAllObjectsVisible = Object.values(visibleObjects).every(
    (value) => value,
  )

  const toggleObjectVisibility = (objectType: TemplateWarningItems) => {
    setVisibleObjects((prev) => ({
      ...prev,
      [objectType]: !prev[objectType],
    }))
  }

  const toggleAllObjects = (value: boolean) => {
    const updatedVisibility: ObjectVisibility = {}
    detectedObjectTypes.forEach((type) => {
      updatedVisibility[type] = value
    })
    setVisibleObjects(updatedVisibility)
  }

  // Bug where the map renders thin after expanding from collapsed state because width is animated and starts from 0
  // resize map when sidebar is expanded so it gets correct dimensions after expanding completes
  useEffect(() => {
    if (!expanded || !hasLocationMetadata) return
    const id = window.setTimeout(() => {
      const map = mapRef.current?.getMap?.()
      if (map?.resize) map.resize()
    }, 200)
    return () => window.clearTimeout(id)
  }, [expanded, hasLocationMetadata])

  return (
    <ModalDialogContainer
      preventDialogClose={preventDialogClose}
      maxWidth={false}
    >
      {title && (
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          <Flex alignItems='center'>
            {title}
            {hasMultipleImages && (
              <Flex ml={1} alignItems='center'>
                <Text fontSize='14px' color='grey.600'>
                  {currentImageIndex + 1} of {imageArray.length}
                </Text>
              </Flex>
            )}
          </Flex>
          <ObjectDetectionRobotChip s3Key={currentImageKey} />
          <Box flexGrow={1} />
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
      )}

      <Flex>
        <Box flex={1}>
          <DialogContent sx={{ pb: 1 }}>
            <Box position='relative'>
              <Image
                width='100%'
                height='100%'
                maxHeight='calc(100vh - 220px)'
                sx={{
                  objectFit: 'contain',
                }}
                src={currentImageUrl}
                onImageRef={(ref) => {
                  if (ref) {
                    const container = ref.parentElement
                    if (container) {
                      // Only update if dimensions have actually changed
                      const newSize = {
                        containerWidth: container.clientWidth,
                        containerHeight: container.clientHeight,
                        naturalWidth: ref.naturalWidth,
                        naturalHeight: ref.naturalHeight,
                      }

                      if (!isEqual(newSize, imageSize)) {
                        setImageSize(newSize)
                      }
                    }
                  }
                }}
              />
              {renderBoundingBoxes()}
              {hasMultipleImages && (
                <CarouselNavigation
                  onPrevious={handlePreviousImage}
                  onNext={handleNextImage}
                />
              )}
            </Box>
          </DialogContent>

          {(!!footerActions || canDownload) && (
            <DialogActions sx={{ justifyContent: 'flex-start' }}>
              {footerActions}
              {canDownload && (
                <ButtonGroup variant='contained'>
                  <Button
                    onClick={async () => {
                      const filename = decodeURIComponent(currentImageUrl)
                        .split('filename=')[1]
                        .split('&')[0]
                        .slice(1, -1)

                      try {
                        const response = await axios.get(currentImageUrl, {
                          responseType: 'blob',
                          headers: {
                            'Cache-Control': 'no-cache',
                          },
                        })
                        fileDownload(response.data, filename)
                      } catch (_) {
                        toast.error(
                          localizedStrings.pleaseRefreshPageAndTryFileDownloadAgain,
                        )
                      }
                    }}
                  >
                    {localizedStrings.download}
                  </Button>
                  {!!onDownloadAll && (
                    <DropdownButton
                      label=''
                      options={[
                        {
                          title: localizedStrings.downloadAll,
                          onSelect: () => onDownloadAll(),
                          icon: <DownloadIcon fontSize='small' />,
                        },
                      ]}
                      renderButton={(onClick) => {
                        return (
                          <Button
                            size='small'
                            sx={{ flex: 1 }}
                            onClick={onClick}
                          >
                            <ArrowDropDownIcon fontSize='small' />
                          </Button>
                        )
                      }}
                    />
                  )}
                </ButtonGroup>
              )}
            </DialogActions>
          )}
        </Box>

        {(detectedObjectTypes.length > 0 || hasLocationMetadata) && (
          <Box
            sx={{
              width: expanded ? '18.5rem' : '4.1rem',
              transition: 'width 0.2s ease-out',
              boxShadow: '-6px 0 10px -5px rgba(0, 0, 0, 0.14)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Flex alignItems='center' top={0}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                sx={{ borderRadius: 0 }}
              >
                <MenuOpenOutlined
                  sx={{
                    transition: 'transform 0.2s ease-out',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </IconButton>

              <Text noWrap sx={{ opacity: expanded ? 1 : 0 }}>
                {detectedObjectTypes.length > 0
                  ? localizedStrings.highlightDetectedObjects
                  : localizedStrings.image}
              </Text>
            </Flex>

            {detectedObjectTypes.length > 0 && (
              <>
                <MenuItem
                  sx={{
                    width: expanded ? '100%' : 'fit-content',
                    borderBottom: 1,
                    borderColor: 'grey.200',
                    mb: 1,
                  }}
                >
                  <Flex
                    alignItems='center'
                    justifyContent='space-between'
                    width='100%'
                  >
                    <Text sx={{ opacity: expanded ? 1 : 0 }}>
                      {localizedStrings.allObjects}
                    </Text>
                    <Switch
                      checked={areAllObjectsVisible}
                      onChange={(e) => toggleAllObjects(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-track': {
                          backgroundColor: areAllObjectsVisible
                            ? 'primary.main'
                            : 'grey.300',
                        },
                      }}
                    />
                  </Flex>
                </MenuItem>

                {detectedObjectTypes.map((objectType) => {
                  const matchingObject = objectDetections
                    .flatMap(
                      (warning) => warning.detectedObjects?.[s3Key] || [],
                    )
                    .find((obj) => obj.displayName === objectType)

                  const colors =
                    warningItemColors[matchingObject?.item] ||
                    warningItemColors.ALL_ITEMS

                  return (
                    <MenuItem
                      key={objectType}
                      sx={{
                        width: expanded ? '100%' : 'fit-content',
                      }}
                    >
                      <Flex
                        alignItems='center'
                        justifyContent='space-between'
                        width='100%'
                      >
                        <Flex alignItems='center'>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: colors.border,
                              mr: 1.5,
                            }}
                          />
                          <Text sx={{ opacity: expanded ? 1 : 0 }}>
                            {`${objectType} (${matchingObject?.count || 0})`}
                          </Text>
                        </Flex>
                        <Switch
                          checked={visibleObjects[objectType] ?? false}
                          onChange={() => toggleObjectVisibility(objectType)}
                          sx={{
                            '& .MuiSwitch-track': {
                              backgroundColor: visibleObjects[objectType]
                                ? 'primary.main'
                                : 'grey.300',
                            },
                          }}
                        />
                      </Flex>
                    </MenuItem>
                  )
                })}
              </>
            )}

            {hasLocationMetadata && expanded && (
              <>
                {locationData?.latitude && locationData?.longitude && (
                  <Box
                    sx={{
                      borderTop: 1,
                      borderColor: 'grey.200',
                      pt: 2,
                      mt: 2,
                      px: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Text fontSize={12} color='grey.600' mb={1}>
                      {localizedStrings.imageLocation}
                    </Text>
                    <Box sx={{ height: 160, borderRadius: 1 }}>
                      <MapBoxMap
                        mapRef={mapRef}
                        mapStyle={constants.mapbox_default_style_url}
                        initialViewState={{
                          latitude: locationData.latitude,
                          longitude: locationData.longitude,
                          zoom: 15,
                        }}
                      >
                        <Marker
                          longitude={locationData.longitude}
                          latitude={locationData.latitude}
                          anchor='bottom'
                        >
                          <Place sx={{ fontSize: 28, color: 'primary.main' }} />
                        </Marker>
                      </MapBoxMap>
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    borderTop: 1,
                    borderColor: 'grey.200',
                    pt: 2,
                    mt: 2,
                    px: 2,
                  }}
                >
                  {capturedByName && (
                    <Box mb={1}>
                      <Text fontSize={12} color='grey.600'>
                        {localizedStrings.capturedBy}
                      </Text>
                      <Text fontSize={14}>{capturedByName}</Text>
                    </Box>
                  )}
                  {locationData?.photoTakenOn && (
                    <Box mb={1}>
                      <Text fontSize={12} color='grey.600'>
                        {localizedStrings.dateCaptured}
                      </Text>
                      <Text fontSize={14}>
                        {moment(locationData.photoTakenOn).format(
                          'DD MMM YYYY HH:mm',
                        )}
                      </Text>
                    </Box>
                  )}
                  {locationData?.latitude && locationData?.longitude && (
                    <Box mb={1}>
                      <Text fontSize={12} color='grey.600'>
                        {localizedStrings.location}
                      </Text>
                      <Text fontSize={14}>
                        {localizedStrings.latLongFormatted(
                          locationData.latitude,
                          locationData.longitude,
                        )}
                      </Text>
                    </Box>
                  )}
                  {locationData?.accuracy && (
                    <Box>
                      <Text fontSize={12} color='grey.600'>
                        {localizedStrings.locationAccuracy}
                      </Text>

                      <Text fontSize={14}>{`${
                        localizedStrings.accuracy
                      }: ${Number(locationData.accuracy).toFixed(2)}m`}</Text>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </Flex>
    </ModalDialogContainer>
  )
}

export default ImageModal
